<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useSharedState } from 'composables/useSharedState';
import type { EditorProps } from './EditorProps';
import { editorStyles } from './Editor.styles';
import { loadMonaco, loadMonacoCss } from './monaco-loader';

/**
 * `<e-editor>`
 *
 * Shadow‑DOM‑safe Monaco editor wrapper:
 * - loads Monaco from a CDN (via `monaco-loader`)
 * - injects Monaco CSS into the component's shadow root
 * - syncs value / language / theme with shared state
 */
const props = withDefaults(defineProps<EditorProps>(), { value: '' });
const emit = defineEmits<{ 'update:value': [string] }>();

const state = useSharedState(props);

const container = ref<HTMLElement | null>(null);
const rootContainer = ref<HTMLElement | null>(null);

let editor: monaco.editor.IStandaloneCodeEditor | null = null;
let contentDisposer: monaco.IDisposable | null = null;
let resizeObserver: ResizeObserver | null = null;

const getMonaco = (): typeof monaco | undefined => {
  return (globalThis as typeof globalThis & { monaco?: typeof monaco }).monaco;
};

const updateEditorHeight = () => {
  if (!editor || !container.value) return;

  const contentHeight = editor.getContentHeight();
  const extraPadding = 12; // small visual margin below the last line
  const height = contentHeight + extraPadding;
  container.value.style.height = `${height}px`;

  // Let Monaco know the container size changed.
  editor.layout();
};

/**
 * Initialize Monaco inside the given shadow root and wire up events/layout.
 */
const initMonaco = async (shadow: ShadowRoot): Promise<void> => {
  if (!container.value) return;

  const hostContainer = container.value;

  // In some embedding scenarios (e.g. docs hot reload or rapid mount/unmount),
  // Vue's mounted hook can fire while the element is not yet fully attached
  // to the DOM. Monaco expects a live DOM node with a parent, so guard here
  // to avoid internal "parentNode is null" errors.
  if (!hostContainer.isConnected || !hostContainer.parentNode) {
    console.error('MonacoCE: Editor container is not attached to the DOM, skipping init.');
    return;
  }

  // Ensure CSS + runtime are ready.
  await loadMonacoCss(shadow);
  await loadMonaco();

  const monacoGlobal = getMonaco();
  if (!monacoGlobal?.editor) {
    console.error('MonacoCE: Monaco global not available after load.');
    return;
  }

  // Resolve Monaco theme, optionally respecting high‑contrast OS settings.
  let resolvedTheme = state.monacoTheme?.value ?? props.monacoTheme ?? 'vs-dark';

  if (props.autoDetectHighContrast !== false && typeof globalThis !== 'undefined') {
    const forcedColors = globalThis.matchMedia?.('(forced-colors: active)');
    const prefersMoreContrast = globalThis.matchMedia?.('(prefers-contrast: more)');

    if (
      (forcedColors && forcedColors.matches) ||
      (prefersMoreContrast && prefersMoreContrast.matches)
    ) {
      if (!/hc/i.test(resolvedTheme)) {
        resolvedTheme = 'hc-black';
      }
    }
  }

  editor = monacoGlobal.editor.create(hostContainer, {
    value: state.value?.value ?? props.value ?? '',
    language: state.language?.value ?? props.language ?? 'javascript',
    theme: resolvedTheme,
    readOnly: props.readOnly ?? false,
    minimap: { enabled: props.minimap ?? false },
    automaticLayout: props.automaticLayout ?? false,
    scrollBeyondLastLine: false,
    accessibilityHelpUrl: props.accessibilityHelpUrl,
    ariaContainerElement: props.ariaContainerElement ?? document.body
  });

  // Apply theme globally as well to ensure it takes effect.
  if (resolvedTheme) {
    monacoGlobal.editor.setTheme(resolvedTheme);
  }

  updateEditorHeight();

  // Keep layout in sync with container resizes.
  resizeObserver = new ResizeObserver(() => {
    // On container resize, just relayout; don't recompute height here to
    // avoid feedback loops between height changes and ResizeObserver.
    editor?.layout();
  });
  resizeObserver.observe(container.value);

  // Emit value changes and keep height updated.
  contentDisposer = editor?.onDidChangeModelContent(() => {
    const value = editor!.getValue();
    updateEditorHeight();
    emit('update:value', value);
  });
};

// Lifecycle
onMounted(async () => {
  const root = rootContainer.value?.getRootNode();
  if (!(root instanceof ShadowRoot)) {
    console.error('MonacoCE: Shadow root not found.', root);
    return;
  }

  await initMonaco(root);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  contentDisposer?.dispose();
  editor?.dispose();

  resizeObserver = null;
  contentDisposer = null;
  editor = null;
});

// Prop / state watchers
watch(
  () => state.value?.value,
  v => {
    if (editor && v != null && editor.getValue() !== v) {
      editor.setValue(v);
      updateEditorHeight();
    }
  }
);

watch(
  () => state.language?.value,
  lang => {
    if (editor && lang) {
      const monacoGlobal = getMonaco();
      const model = editor.getModel();

      if (monacoGlobal?.editor?.setModelLanguage && model) {
        monacoGlobal.editor.setModelLanguage(model, lang);
      } else {
        editor.updateOptions({ language: lang });
      }
    }
  }
);

watch(
  () => state.monacoTheme?.value,
  theme => {
    if (theme) {
      const monacoGlobal = getMonaco();
      if (monacoGlobal?.editor?.setTheme) {
        monacoGlobal.editor.setTheme(theme);
      }
    }
  }
);
</script>

<template>
  <div ref="rootContainer">
    <div
      ref="container"
      class="editor-root"
      :class="editorStyles(props)"
    ></div>
  </div>
</template>
